import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QuickAddService from '../components/services/QuickAddService';
import { useAccountSummary } from './AccountSummaryContext';

interface OpenOptions {
  initialText?: string;
  /** Se dispara en cuanto el servicio se crea */
  onCreated?: (servicioId: number) => void;
  /** Se dispara al cerrar el modal; createdAny indica si se creó algo durante la sesión */
  onClose?: (createdAny: boolean) => void;
}

interface QuickAddValue {
  openQuickAdd: (options?: OpenOptions) => void;
}

const QuickAddContext = createContext<QuickAddValue | null>(null);

export const QuickAddServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { noteCreated } = useAccountSummary();
  const [isOpen, setIsOpen] = useState(false);
  const [initialText, setInitialText] = useState('');
  const optionsRef = useRef<OpenOptions>({});
  const createdRef = useRef(false);

  const openQuickAdd = useCallback((options: OpenOptions = {}) => {
    optionsRef.current = options;
    createdRef.current = false;
    setInitialText(options.initialText || '');
    setIsOpen(true);
  }, []);

  const handleCreated = useCallback((servicioId: number) => {
    createdRef.current = true;
    noteCreated('servicio');
    optionsRef.current.onCreated?.(servicioId);
  }, [noteCreated]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    optionsRef.current.onClose?.(createdRef.current);
  }, []);

  return (
    <QuickAddContext.Provider value={{ openQuickAdd }}>
      {children}
      <QuickAddService
        isOpen={isOpen}
        onClose={handleClose}
        onCreated={handleCreated}
        initialText={initialText}
      />
    </QuickAddContext.Provider>
  );
};

/**
 * Fuera del provider degrada al formulario largo en vez de romper.
 */
export const useQuickAdd = (): QuickAddValue => {
  const ctx = useContext(QuickAddContext);
  const navigate = useNavigate();
  if (ctx) return ctx;
  return { openQuickAdd: () => navigate('/servicios/nuevo') };
};
